import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `<MultiLanguageAccessView />` — patient-facing locale preference
 * + accommodation pathway for non-supported languages.
 *
 * Per Tech Spec v4.2 §6.1 + PFO Spec v1.4 §7.7 + PFO §2.3
 * accommodation discipline:
 *
 *   - v1 PHG ships multi-language patient-facing access at priority
 *     languages per the SHT language-access policy
 *   - The policy specifies priority languages at launch, criteria
 *     for adding languages, operational SLA for translation in
 *     non-priority languages, and patient-facing communications in
 *     non-supported languages
 *   - Accommodation is a first-class branch, not an error path —
 *     UX must not stigmatize accommodation per PFO §2.3
 *     onboarding-cost acknowledgment
 *
 * **Doctrinal posture (load-bearing):**
 *
 *   1. **Engineering does not name priority languages.** The list
 *      of supported languages is set by the SHT language-access
 *      policy. v0 ships English only because the policy has not
 *      yet crystallized. The view exposes this disclosure honestly:
 *      "the list of supported languages is set by the SHT
 *      language-access policy, not by engineering."
 *   2. **Accommodation is patient-affirmative.** When the patient's
 *      preferred locale is not supported, the view surfaces three
 *      explicit accommodation pathways drawn from PFO §2.3 +
 *      §7.7. The patient affirmatively elects which (or none); no
 *      pre-selected default.
 *   3. **PHG cannot promise instant translation coverage.** Honesty
 *      framing: human-translation accommodation operates under the
 *      SHT language-access policy SLA. UX must surface the SLA
 *      framing rather than implying the request will be fulfilled
 *      immediately.
 *
 * **Patient-Facing Terms v1.3 doctrinal-copy gate** (coord issue
 * #42): the canonical patient-facing copy for non-supported-locale
 * disclosure ships when v1.3 lands. v0 placeholder copy aligned
 * with PFO §2.3 + §7.7.
 */
import * as React from 'react';
import { useLocale } from '../i18n/locale-context.js';
const ACCOMMODATION_KINDS = [
    'human-translation',
    'related-language-fallback',
    'critical-communications-only',
];
const DEFAULT_CANDIDATE_LOCALES = [
    'en-US',
    'es-MX',
    'es-ES',
    'zh-Hans',
    'fr-FR',
    'ar-SA',
    'vi-VN',
];
export function MultiLanguageAccessView(props) {
    const locale = useLocale();
    const [accommodationRequested, setAccommodationRequested] = React.useState(false);
    const [pendingChoice, setPendingChoice] = React.useState(locale.requestedLocale);
    const candidateLocales = props.candidateLocales ??
        Array.from(new Set([...DEFAULT_CANDIDATE_LOCALES, ...locale.availableLocales])).sort();
    const requestAccommodation = (_kind) => {
        // v0 stub: the accommodation request would dispatch to SHT
        // operational support routing infrastructure. v1 wires this
        // through a backend service surface (TBD per the SHT
        // language-access policy operational dependency).
        setAccommodationRequested(true);
    };
    return (_jsxs("section", { "aria-labelledby": "lang-heading", children: [_jsx("h2", { id: "lang-heading", children: locale.t('language.heading') }), _jsx("p", { children: locale.t('language.intro_body') }), _jsxs("p", { children: [_jsxs("strong", { children: [locale.t('language.current_locale_label'), ":"] }), " ", locale.locale] }), _jsx("h3", { children: locale.t('language.available_locales_heading') }), _jsx("ul", { "aria-label": locale.t('language.available_locales_aria_label'), children: locale.availableLocales.map((tag) => (_jsx("li", { children: _jsx("button", { type: "button", onClick: () => locale.setLocale(tag), "aria-current": locale.locale === tag ? 'true' : undefined, children: tag }) }, tag))) }), _jsx("h3", { children: "Request another language" }), _jsx("p", { children: locale.t('language.policy_dependency_disclosure') }), _jsxs("label", { children: ["Preferred language (BCP-47 tag)", _jsx("select", { value: pendingChoice, onChange: (e) => setPendingChoice(e.target.value), "aria-label": "Preferred language", children: candidateLocales.map((tag) => (_jsx("option", { value: tag, children: tag }, tag))) })] }), _jsx("button", { type: "button", onClick: () => {
                    locale.setLocale(pendingChoice);
                    setAccommodationRequested(false);
                }, children: locale.t('language.set_preference_button') }), !locale.requestedLocaleSupported ? (_jsxs("section", { "aria-labelledby": "accommodation-heading", children: [_jsx("h3", { id: "accommodation-heading", children: locale.t('language.accommodations_heading') }), _jsx("p", { children: locale.t('language.fallback_disclosure', {
                            fallback: locale.locale,
                        }) }), _jsx("ul", { "aria-label": "Accommodation pathways", children: ACCOMMODATION_KINDS.map((kind) => (_jsxs("li", { children: [_jsx("strong", { children: locale.t(`language.accommodation.${kind.replace(/-/g, '_')}.label`) }), ' — ', locale.t(`language.accommodation.${kind.replace(/-/g, '_')}.description`), _jsx("div", { children: _jsx("button", { type: "button", onClick: () => requestAccommodation(kind), children: locale.t('language.request_accommodation_button') }) })] }, kind))) }), accommodationRequested ? (_jsx("p", { role: "status", children: locale.t('language.accommodation_requested_notice') })) : null] })) : null] }));
}
//# sourceMappingURL=multi-language-access-view.js.map